import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Collapse,
  Stack,
  Typography,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { COLORS, FONTS } from "./Hero.styles";

const API_URL = process.env.REACT_APP_API_URL || "";

const INITIAL_FORM   = { name: "", email: "", contact: "", purpose: "" };
const INITIAL_ERRORS = { name: "", email: "", contact: "", purpose: "" };

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <line x1="1" y1="1" x2="17" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="17" y1="1" x2="1" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/**
 * TalkToExpertsDialog
 *
 * Business logic preserved exactly from the original EnterpriseHero:
 *   POST {API_URL}/expert/contact/ with { name, email, contact, purpose }
 *   Same validation rules, same error message fallback chain,
 *   same success auto-close after 2.8s.
 *
 * Visual treatment restyled to match the V2 design tokens (hero.styles.js)
 * but functionally identical to the original dialog.
 */
export default function TalkToExpertsDialog({ open, onClose }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(INITIAL_FORM);
      setErrors(INITIAL_ERRORS);
      setLoading(false);
      setSuccess(false);
      setApiError("");
    }
  }, [open]);

  const validate = () => {
    const errs = { ...INITIAL_ERRORS };
    let valid = true;
    if (!form.name.trim()) { errs.name = "Name is required"; valid = false; }
    if (!form.email.trim()) { errs.email = "Email is required"; valid = false; }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { errs.email = "Enter a valid email"; valid = false; }
    if (!form.contact.trim()) { errs.contact = "Contact number is required"; valid = false; }
    else if (!/^\+?[\d\s\-().]{7,20}$/.test(form.contact)) { errs.contact = "Enter a valid number"; valid = false; }
    if (!form.purpose.trim()) { errs.purpose = "Please describe your purpose"; valid = false; }
    setErrors(errs);
    return valid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (apiError) setApiError("");
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    setApiError("");
    try {
      await axios.post(`${API_URL}/expert/contact/`, {
        name: form.name.trim(),
        email: form.email.trim(),
        contact: form.contact.trim(),
        purpose: form.purpose.trim(),
      });
      setSuccess(true);
      setTimeout(() => onClose(), 2800);
    } catch (err) {
      setApiError(
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const panelVariants = {
    hidden: { opacity: 0, y: 32, scale: 0.97 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit: { opacity: 0, y: 20, scale: 0.98, transition: { duration: 0.22, ease: [0.55, 0, 0.78, 0] } },
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: "rgba(255,255,255,0.04)",
      borderRadius: "10px",
      fontFamily: FONTS.body,
      color: "rgba(255,255,255,0.88)",
      transition: "background 0.2s",
      "&:hover": { background: "rgba(30,144,255,0.05)" },
      "&.Mui-focused": { background: "rgba(30,144,255,0.07)" },
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.border },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(30,144,255,0.35)" },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: COLORS.primary,
      borderWidth: "1.5px",
    },
    "& .MuiInputLabel-root": { fontFamily: FONTS.body, color: COLORS.textFaint, fontSize: "0.9rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: COLORS.primary },
    "& .MuiFormHelperText-root": { fontFamily: FONTS.body, fontSize: "0.76rem" },
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { background: "transparent", boxShadow: "none", overflow: "visible" } }}
      BackdropProps={{ sx: { backdropFilter: "blur(10px)", background: "rgba(0,0,0,0.7)" } }}
      sx={{ "& .MuiDialog-container": { alignItems: "center" } }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            key="expert-panel-v2"
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <DialogContent
              sx={{
                background: "rgba(6,8,14,0.98)",
                border: `1px solid ${COLORS.border}`,
                borderRadius: "18px",
                p: { xs: "26px 20px", sm: "32px 30px" },
              }}
            >
              <IconButton
                onClick={onClose}
                disabled={loading}
                aria-label="Close dialog"
                sx={{
                  position: "absolute",
                  top: 14,
                  right: 14,
                  color: "rgba(255,255,255,0.4)",
                  width: 34,
                  height: 34,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: "9px",
                  "&:hover": { color: "rgba(255,255,255,0.85)", background: "rgba(30,144,255,0.1)" },
                }}
              >
                <CloseIcon />
              </IconButton>

              <Box sx={{ mb: 3, pr: 3 }}>
                <Typography
                  sx={{
                    fontFamily: FONTS.body,
                    fontSize: "0.72rem",
                    color: "rgba(30,144,255,0.82)",
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    mb: 1,
                  }}
                >
                  Expert connect
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONTS.display,
                    fontWeight: 800,
                    fontSize: { xs: "1.5rem", sm: "1.8rem" },
                    color: COLORS.white,
                    letterSpacing: "-0.03em",
                    lineHeight: 1.1,
                    mb: 1,
                  }}
                >
                  Talk to our experts
                </Typography>
                <Typography sx={{ fontFamily: FONTS.body, fontSize: "0.85rem", color: COLORS.textFaint, lineHeight: 1.6 }}>
                  Share your requirements and our enterprise AI specialists will reach out within 24 hours.
                </Typography>
              </Box>

              <AnimatePresence mode="wait">
                {success ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.35 } }}
                    exit={{ opacity: 0 }}
                  >
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: "50%",
                          background: "rgba(30,144,255,0.1)",
                          border: `1.5px solid ${COLORS.primary}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 2.5,
                        }}
                      >
                        <svg width="24" height="24" viewBox="0 0 28 28" fill="none">
                          <path d="M6 14.5L11.5 20L22 9" stroke={COLORS.primary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </Box>
                      <Typography sx={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: "1.2rem", color: COLORS.white, mb: 1 }}>
                        Request received
                      </Typography>
                      <Typography sx={{ fontFamily: FONTS.body, fontSize: "0.86rem", color: COLORS.textFaint, lineHeight: 1.7 }}>
                        Our experts will contact you shortly.
                      </Typography>
                    </Box>
                  </motion.div>
                ) : (
                  <motion.div key="form" initial={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <Collapse in={!!apiError}>
                      <Alert
                        severity="error"
                        sx={{
                          mb: 2.4,
                          borderRadius: "9px",
                          background: "rgba(226,75,74,0.08)",
                          border: "1px solid rgba(226,75,74,0.2)",
                          color: "rgba(255,150,150,0.95)",
                          fontFamily: FONTS.body,
                          fontSize: "0.84rem",
                        }}
                      >
                        {apiError}
                      </Alert>
                    </Collapse>

                    <Stack spacing={2.2}>
                      <TextField name="name" label="Full name" value={form.name} onChange={handleChange} error={!!errors.name} helperText={errors.name} fullWidth disabled={loading} autoComplete="name" sx={fieldSx} />
                      <TextField name="email" label="Work email" type="email" value={form.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} fullWidth disabled={loading} autoComplete="email" sx={fieldSx} />
                      <TextField name="contact" label="Contact number" type="tel" value={form.contact} onChange={handleChange} error={!!errors.contact} helperText={errors.contact} fullWidth disabled={loading} autoComplete="tel" sx={fieldSx} />
                      <TextField name="purpose" label="What would you like to discuss?" value={form.purpose} onChange={handleChange} error={!!errors.purpose} helperText={errors.purpose} fullWidth multiline rows={3} disabled={loading} sx={fieldSx} />
                    </Stack>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={loading}
                      disableElevation
                      sx={{
                        mt: 3,
                        py: 1.5,
                        borderRadius: "11px",
                        textTransform: "none",
                        fontFamily: FONTS.body,
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        background: COLORS.primary,
                        "&:hover": { background: COLORS.primaryLight },
                        "&.Mui-disabled": { background: "rgba(30,144,255,0.25)", color: "rgba(255,255,255,0.4)" },
                      }}
                    >
                      {loading ? (
                        <Stack direction="row" alignItems="center" spacing={1.2}>
                          <CircularProgress size={16} sx={{ color: "rgba(255,255,255,0.6)" }} />
                          <span>Submitting…</span>
                        </Stack>
                      ) : (
                        "Connect with an expert"
                      )}
                    </Button>
                    <Typography sx={{ mt: 1.6, textAlign: "center", fontFamily: FONTS.body, fontSize: "0.74rem", color: "rgba(255,255,255,0.22)" }}>
                      We respect your privacy. No spam, ever.
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>
            </DialogContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Dialog>
  );
}